import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ClientService } from '@app/core/services/client.service';
import { catchError, take } from 'rxjs/operators';
import { SmsCustomKeyword } from '@app/core/models/sms-custom-keyword';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DataMappingUtils } from '@app/core/utils/data-mapping-utils';
import { ValidationUtils } from '@app/core/utils/validation-utils';
import { forkJoin, of } from 'rxjs';
import { Client } from '@app/core/models/client';

/**
 * The client and shortcode each form's keyword list was loaded for. The list
 * lives in the page's form, so it outlives this section.
 */
const loadedFor = new WeakMap<UntypedFormArray, string>();

@Component({
    selector: 'rn-client-sms-custom-keywords',
    templateUrl: './client-sms-custom-keywords.component.html',
    styleUrls: ['./client-sms-custom-keywords.component.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule]
})
export class ClientSmsCustomKeywordsComponent implements OnInit, OnChanges {
  @Input() shortCode: string;
  @Input() shortCodeGroup: UntypedFormGroup;
  @Input() client: Client;
  /**
   * The keywords whose checkboxes show, or all of them when null. The rest
   * stay in the form, as saved. With multi-language STOP/HELP on only
   * stopall shows (DIS-1810).
   */
  @Input() shownKeywords: string[] | null = null;

  disableCheckboxes: boolean = false;
  customKeywordArray: UntypedFormArray = new UntypedFormArray([]);
  masterList: SmsCustomKeyword[];

  constructor(
    public clientConfigService: ClientService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const reopened =
      changes['shortCodeGroup']?.firstChange && this.reuseLoadedList();
    // Reloading would replace checks that haven't been saved yet.
    if (reopened && this.customKeywordArray.dirty) {
      this.toggleDisabledOnCheckboxes();
      return;
    }
    this.handleInputChanges();
  }

  ngOnInit(): void {
    // setControl, not addControl: when the flag swaps one of these sections
    // for another, the one on the page has to be the one Save reads.
    this.shortCodeGroup.setControl(
      'custom_sms_keywords',
      this.customKeywordArray,
    );
  }

  /**
   * Collapsing Short/Long Code Setup destroys this section, but its list stays
   * in the form. Takes that list back if it was loaded for this client and
   * shortcode, so Save reads the list on the page, unsaved checks included.
   * @private
   */
  private reuseLoadedList(): boolean {
    const list = this.shortCodeGroup.get('custom_sms_keywords');
    if (
      !(list instanceof UntypedFormArray) ||
      loadedFor.get(list) !== this.loadKey()
    ) {
      return false;
    }
    this.customKeywordArray = list;
    return true;
  }

  private loadKey(): string {
    return `${this.client?.id}/${this.shortCode}`;
  }

  /**
   * Whether the keyword's checkbox shows (see shownKeywords).
   * @param control
   */
  isShown(control: AbstractControl): boolean {
    return (
      this.shownKeywords === null ||
      this.shownKeywords.includes(
        this.getFormGroup(control, 'custom_sms_keyword').get('keyword').value,
      )
    );
  }

  /**
   * Handles casting the control appropriately.
   * @param control
   * @param name
   */
  getFormControl(control: AbstractControl, name: string): UntypedFormControl {
    const found = control.get(name);
    if (found instanceof UntypedFormControl) {
      return found as UntypedFormControl;
    } else {
      return null;
    }
  }

  /**
   * Handles casting the control appropriately.
   * @param control
   * @param name
   */
  getFormGroup(control: AbstractControl, name: string): UntypedFormGroup {
    const found = control.get(name);
    if (found instanceof UntypedFormGroup) {
      return found as UntypedFormGroup;
    } else {
      return null;
    }
  }

  /**
   * Rebuilds the hierarchy for the UI form controls. Note that the checked formcontrol relies on
   * this.disableCheckBoxes to determine whether the input should be enabled or disabled.
   * @param masterList
   * @param activeList
   */
  rebuildControlHierarchy(
    masterList: SmsCustomKeyword[],
    activeList: SmsCustomKeyword[] = [],
  ): void {
    this.customKeywordArray.clear();
    const stringifiedActiveList = activeList.map((keyword) => {
      return JSON.stringify(keyword);
    });

    masterList.map((keyword) => {
      const uiGroup = new UntypedFormGroup({});
      const keywordGroup = new UntypedFormGroup({});
      const stringifiedKeyword = JSON.stringify(keyword);
      const isChecked = stringifiedActiveList.some((activeKeyword) => {
        return stringifiedKeyword === activeKeyword;
      });
      const keys = Object.keys(keyword);

      uiGroup.addControl(
        'checked',
        new UntypedFormControl({ value: isChecked, disabled: this.disableCheckboxes }),
      );
      uiGroup.addControl('custom_sms_keyword', keywordGroup);
      keys.map((key) => {
        keywordGroup.addControl(key, new UntypedFormControl(keyword[key]));
      });

      this.customKeywordArray.push(uiGroup);
    });
  }

  /**
   * If we don't have a good value for the shortCode, don't bother trying to call out.
   * Otherwise, check against the shared shortCode and enable/disable accordingly.
   * @private
   */
  handleInputChanges(): void {
    this.toggleDisabledOnCheckboxes();
    this.getCustomSmsKeywords();
  }

  /**
   * Disables checkboxes if the shortcode is invalid either because it is empty or
   * the shortcode matches the shared code.
   */
  toggleDisabledOnCheckboxes(): boolean {
    this.disableCheckboxes = ValidationUtils.isStringEmptyOrNull(
      this.shortCode,
    );
    return this.disableCheckboxes;
  }

  /**
   * Helper utility to turn the active records into a list for ease of access.
   * @param apiActiveList
   * @private
   */
  convertServiceKeywordsToModelKeywords(apiActiveList): SmsCustomKeyword[] {
    let result = [];

    if (
      apiActiveList.custom_sms_keywords &&
      Object.keys(apiActiveList.custom_sms_keywords).length > 0
    ) {
      result = DataMappingUtils.entriesPolyFill(
        apiActiveList.custom_sms_keywords,
      ).map((value) => {
        return SmsCustomKeyword.deserialize(value[0], value[1]);
      });
    }

    return result;
  }

  /**
   * Once the lookup keys are in place, calls out to the service to pull the sms keywords for the client.
   * Then it builds it into an AbstractControl collection, which then feeds the
   * checkboxes in the UI.
   * @private
   */
  getCustomSmsKeywords(): void {
    if (
      ValidationUtils.isStringEmptyOrNull(this.client.id) ||
      ValidationUtils.isStringEmptyOrNull(this.shortCode)
    ) {
      return;
    }

    const key = this.loadKey();
    forkJoin({
      apiMasterList: this.clientConfigService.getMasterCustomSMSKeywords(),
      apiActiveList: this.clientConfigService
        .getCustomSMSKeywords(this.client.id, this.shortCode)
        .pipe(catchError((error) => of([]))),
    })
      .pipe(take(1))
      .subscribe(
        ({ apiMasterList, apiActiveList }) => {
          const activeList = apiActiveList.custom_sms_keywords
            ? this.convertServiceKeywordsToModelKeywords(apiActiveList)
            : [];
          this.rebuildControlHierarchy(apiMasterList, activeList);
          loadedFor.set(this.customKeywordArray, key);
        },
        (error) => {
          this.rebuildControlHierarchy([], []);
        },
      );
  }
}
